import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Icon,
  Tooltip,
  Box,
  InlineStack,
  Badge,
  // ButtonGroup,
  Button,
  Popover,
  ActionList,
} from "@shopify/polaris";
import {
  ExternalIcon,
  ConnectIcon,
  MenuHorizontalIcon,
} from "@shopify/polaris-icons";

import PaymentMethodResourceItem from "../Stripe/PaymentMethodResourceItem";

const PaymentMethodCard = (props) => {
  const {
    validatePurchaseMethod,
    stripeSetupIntent,
    opportunity,
    paymentMethod,
    allPaymentMethodsLength,
    index,
  } = props;

  const [active, setActive] = useState(false);
  const [stripeLink, setStripeLink] = useState(null);

  const toggleActive = useCallback(() => setActive((active) => !active), []);

  // Generate the appropriate Stripe link based on the payment method type
  useEffect(() => {
    console.log("PaymentMethodCard props:", {
      "opportunity.stripe_customer_link": opportunity?.attributes?.stripe_customer_link,
      "stripeSetupIntent.customer": stripeSetupIntent?.attributes?.customer,
      "paymentMethod.destination": paymentMethod?.destination,
      "paymentMethod.customer": paymentMethod?.customer,
    });
    
    // Generate Stripe link based on payment method destination
    let link = null;
    const isProduction = process.env.NODE_ENV === 'production';
    const baseUrl = "https://dashboard.stripe.com";
    const testPath = isProduction ? "" : "test";
    
    // Check if this is a connected account payment method
    if (paymentMethod?.destination === "connected") {
      // For connected account payment methods, use the opportunity's stripe_customer_link
      if (opportunity?.attributes?.stripe_customer_link) {
        link = opportunity.attributes.stripe_customer_link;
      }
      // If stripe_customer_link is missing but we have the customer ID, build the link
      else if (paymentMethod?.customer) {
        const customerId = paymentMethod.customer;
        // Note: For connected accounts, we'd need the connected account ID which 
        // we don't seem to have directly in the payment method object
        link = `${baseUrl}/${testPath}/customers/${customerId}`;
      }
    } 
    // Check if this is a platform payment method
    else if (paymentMethod?.destination === "platform") {
      // For platform payment methods, build a link to the platform Stripe dashboard
      if (paymentMethod?.customer) {
        // Use the customer ID from the payment method if available
        const customerId = paymentMethod.customer;
        link = `${baseUrl}/${testPath}/customers/${customerId}`;
      }
      else if (stripeSetupIntent?.attributes?.customer) {
        // Fall back to the setup intent customer ID if needed
        const customerId = stripeSetupIntent.attributes.customer;
        link = `${baseUrl}/${testPath}/customers/${customerId}`;
      }
    }
    // If no specific destination is set, try our best
    else {
      // First try the opportunity's stripe_customer_link (vendor connected account)
      if (opportunity?.attributes?.stripe_customer_link) {
        link = opportunity.attributes.stripe_customer_link;
      } 
      // Then try to build a link from the setup intent customer ID (platform account)
      else if (stripeSetupIntent?.attributes?.customer) {
        const customerId = stripeSetupIntent.attributes.customer;
        link = `${baseUrl}/${testPath}/customers/${customerId}`;
      }
      // Lastly, try to use the payment method's customer ID
      else if (paymentMethod?.customer) {
        const customerId = paymentMethod.customer;
        link = `${baseUrl}/${testPath}/customers/${customerId}`;
      }
    }
    
    console.log("Generated Stripe link:", link);
    setStripeLink(link);
  }, [opportunity, stripeSetupIntent, paymentMethod]);

  const activator = (
    <Button
      textAlign="left"
      variant="tertiary"
      onClick={toggleActive}
      icon={MenuHorizontalIcon}
    />
  );

  // Create action items for the popover menu
  const actionItems = [];
  
  // Add "View in Stripe" if we have a valid link
  if (stripeLink) {
    actionItems.push({
      content: "View in Stripe",
      suffix: <Icon source={ExternalIcon} />,
      external: true,
      target: "_blank",
      url: stripeLink,
    });
  }
  
  // Always add the "Validate" option
  actionItems.push({
    content: 'Validate',
    onAction: validatePurchaseMethod,
    disabled: (
      stripeSetupIntent?.attributes?.valid &&
      stripeSetupIntent?.attributes?.status === "valid_payment"
    )
  });

  return (
    <Box
      padding="400"
      paddingBlockEnd={
        index + 1 === allPaymentMethodsLength ? "400" : "0"
      }
      as="section"
      key={index}
    >
      <InlineStack align="space-between" wrap={false}>
        <InlineStack
          align="space-between"
          blockAlign={"center"}
          wrap={false}
          gap="200"
        >
          <PaymentMethodResourceItem
            id={paymentMethod.id}
            card={paymentMethod.card}
            us_bank_account={paymentMethod.us_bank_account}
            acss_debit={paymentMethod.acss_debit}
            resourceItem={false}
          />

          {paymentMethod?.destination == "connected" && (
            <Tooltip content={"Connected account"}>
              <Icon source={ConnectIcon} tone={"interactive"} />
            </Tooltip>
          )}

          {stripeSetupIntent?.attributes?.status ===
            "valid_payment" && <Badge tone="success">Valid</Badge>}
          {stripeSetupIntent?.attributes?.status ===
            "failed_payment" && (
            <Badge tone="critical">Failed</Badge>
          )}
          {stripeSetupIntent?.attributes?.status === "pending" && (
            <Badge>Pending</Badge>
          )}
        </InlineStack>

        <Popover
          active={active}
          activator={activator}
          autofocusTarget="first-node"
          onClose={toggleActive}
        >
          <ActionList
            actionRole="menuitem"
            items={actionItems}
          />
        </Popover>
      </InlineStack>
    </Box>
  );
};


PaymentMethodCard.propTypes = {
  opportunity: PropTypes.object,
  paymentMethod: PropTypes.object,
  index: PropTypes.number,
  validatePurchaseMethod: PropTypes.func,
  stripeSetupIntent: PropTypes.object,
  allPaymentMethodsLength: PropTypes.number,
};

export default PaymentMethodCard;
